import React from 'react';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'neutral' | 'info' | 'default' | 'outline';
  size?: 'sm' | 'md';
  className?: string;
}

// forwardRef so wrappers that need a DOM handle on the trigger (e.g. Tooltip,
// which clones its child and attaches a ref for positioning) work without the
// "Function components cannot be given refs" warning. Extra props are spread onto
// the span so those wrappers' injected handlers/aria attributes actually apply.
export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { children, variant = 'neutral', size = 'md', className = '', ...rest },
  ref,
) {
  const variants = {
    primary: 'bg-primary-100 text-primary-700',
    secondary: 'bg-secondary-100 text-secondary-700',
    success: 'bg-green-100 text-green-700',
    warning: 'bg-amber-100 text-amber-700',
    error: 'bg-red-100 text-red-700',
    neutral: 'bg-neutral-100 text-neutral-700',
    info: 'bg-blue-100 text-blue-700',
    default: 'bg-neutral-50 text-neutral-700',
    outline: 'bg-transparent border border-gray-300 text-gray-700',
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
  };

  return (
    <span
      ref={ref}
      className={`inline-flex items-center font-medium rounded-full ${variants[variant]} ${sizes[size]} ${className}`}
      {...rest}
    >
      {children}
    </span>
  );
});
